import { IconNamesGenerated } from '@embracesbs/component-icon/IconNames.generated'

// The sidebar's contents, in one place, because two screens read them: the
// sidebar itself (MainMenu) and the Developer center, which lists the developer
// tools as cards.
//
// The sidebar stays short and never changes shape: Home, Advanced Search and
// Developer center, with Settings and logout pinned at the bottom. The developer
// tools are reached through the Developer center rather than listed in the rail,
// which is what keeps it short. That costs a click on the way to a tool, and in
// exchange there is no mode to switch, nothing to remember, and no question
// about which rail a tenant page belongs to. If one tool turns out to be used
// constantly it can be moved into SIDEBAR_ITEMS on its own; its adminOnly flag
// travels with it, because both lists are filtered through navItemsFor.
//
// Role gating is per item and unchanged: the three admin-only tools stay
// admin-only, and everything that was open to any tenant.list user still is.

export interface NavItem {
  id: string
  title: string
  path: string
  // Typed against the design system's own icon set, so a name that does not
  // exist is a compile error rather than a blank square in the sidebar.
  icon: IconNamesGenerated
  adminOnly?: boolean
  // Extra paths that also light this item up, for pages that live outside the
  // item's own path. `path` always matches, so most items need none.
  activePrefixes?: string[]
}

// A developer tool additionally needs the line of copy its Developer center card
// shows. The sidebar is a rail, so it has no room for one.
export interface DeveloperTool extends NavItem {
  description: string
}

export const DEVELOPER_CENTER_PATH = '/developer'

// The tools the Developer center offers. Not in the sidebar: the center is the
// way in.
const DEVELOPER_ITEMS: DeveloperTool[] = [
  {
    id: 'modules',
    title: 'Terraform Modules',
    description: 'Which module version every tenant runs, and what the newest one is.',
    path: '/modules',
    icon: 'DataFileGraph'
  },
  {
    id: 'feature-flags',
    title: 'Feature Flags',
    description: 'Read and toggle the product feature flags per environment.',
    path: '/feature-flags',
    icon: 'FlagPlain3'
  },
  {
    id: 'reports',
    title: 'Reports',
    description: 'Cluster resource-usage and vulnerability scan reports.',
    path: '/reports',
    icon: 'GraphStats'
  },
  {
    id: 'bulk-update',
    title: 'Bulk Update',
    description: 'Update many tenants to a module version in one run.',
    path: '/bulk-update',
    icon: 'Connect',
    adminOnly: true
  },
  {
    id: 'rollout',
    title: 'Stage Rollout',
    description: 'Roll a release through a stage, a batch of tenants at a time.',
    path: '/rollout',
    icon: 'SpaceRocketFlying',
    adminOnly: true
  },
  {
    id: 'config-migration',
    title: 'Config Migration',
    description: 'Backfill historical tenant configuration into SQL, per tenant.',
    path: '/config-migration',
    icon: 'Swap',
    adminOnly: true
  },
  {
    id: 'certificates',
    title: 'Certificates',
    description: 'The certificate issuers a tenant hostname can use instead of the default.',
    path: '/certificates',
    icon: 'LegalCertificate',
    adminOnly: true
  }
]

export const SIDEBAR_ITEMS: NavItem[] = [
  {
    id: 'home',
    title: 'Home',
    path: '/',
    icon: 'Home',
    // The dashboard also serves the subscription and tenant detail routes.
    activePrefixes: ['/subscriptions']
  },
  {
    id: 'search',
    title: 'Advanced Search',
    path: '/search',
    icon: 'Search'
  },
  {
    id: 'developer-center',
    title: 'Developer center',
    path: DEVELOPER_CENTER_PATH,
    icon: 'ToolBox',
    // Highlights for its own page AND for every tool, so while you are inside a
    // tool the rail still says which part of the app you are in.
    activePrefixes: DEVELOPER_ITEMS.map((item) => item.path)
  }
]

// Matches on a path segment boundary, so /modules lights up for /modules and
// /modules/anything but not for a future /modules-archive.
const matchesPath = (pathname: string, path: string): boolean =>
  path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`)

export const isNavItemActive = (item: NavItem, pathname: string): boolean =>
  matchesPath(pathname, item.path) || (item.activePrefixes ?? []).some((prefix) => matchesPath(pathname, prefix))

// The items this user may see. Both lists go through here, so an adminOnly entry
// is gated wherever it is rendered.
export const navItemsFor = <T extends NavItem>(items: T[], isAdmin: boolean): T[] => items.filter((item) => !item.adminOnly || isAdmin)

// The tools the Developer center shows. Its sidebar item is open to everyone,
// the same way Terraform Modules, Feature Flags and Reports always were; only
// the three bulk tools are admin-only.
export const developerToolsFor = (isAdmin: boolean): DeveloperTool[] => navItemsFor(DEVELOPER_ITEMS, isAdmin)
